import * as React from 'react'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import { ExplotableItem, ExplotableList } from 'modules/core/components/Explotable'
import SortableBoxList from 'modules/core/components/SortableBoxList'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import Item from 'modules/surveys/pages/QuestionsTemplates/MainContent/QuestionsContent/List/Item'
import type QuestionsTemplateCategory from 'modules/surveys/store/models/QuestionsTemplateCategory'
import type TemplateQuestion from 'modules/surveys/store/models/TemplateQuestion'

import Header from './_Header'

type HeaderProps = React.ComponentProps<typeof Header>
type ItemProps = React.ComponentProps<typeof Item>

interface Props {
  questionsTemplateCategory: QuestionsTemplateCategory
  createTemplateQuestion: HeaderProps['createTemplateQuestion']
  updateTemplateQuestion: ItemProps['updateTemplateQuestion']
  reorderTemplateQuestion: (config: { id: number; newPosition: number }) => void
  deleteTemplateQuestion: ItemProps['deleteTemplateQuestion']
  isCore: boolean
}

function QuestionCategoryComponent({
  questionsTemplateCategory,
  createTemplateQuestion,
  updateTemplateQuestion,
  reorderTemplateQuestion,
  deleteTemplateQuestion,
  isCore,
}: Props) {
  const [open, toggle] = useToggle(false)

  const updateQuestionsTemplateCategory: HeaderProps['updateQuestionsTemplateCategory'] = async (
    data
  ) => {
    const { value } = await asyncAction(() => questionsTemplateCategory.save(data))

    return value
  }

  const deleteQuestionsTemplateCategory: HeaderProps['deleteQuestionsTemplateCategory'] = async (
    _data
  ) => {
    const { value } = await asyncAction(() => questionsTemplateCategory.destroy())

    return value
  }

  const questionsHeader = (open: boolean) => (
    <Header
      questionsTemplateCategory={questionsTemplateCategory}
      createTemplateQuestion={createTemplateQuestion}
      updateQuestionsTemplateCategory={updateQuestionsTemplateCategory}
      deleteQuestionsTemplateCategory={deleteQuestionsTemplateCategory}
      expanded={open}
      isCore={isCore}
    />
  )

  const templateQuestions = sortBy(questionsTemplateCategory.templateQuestions, (question) =>
    question.get('position')
  )

  const onSortEnd = (items: TemplateQuestion[]) => {
    items.forEach((item, index) => {
      reorderTemplateQuestion({
        id: item.attributes.id,
        newPosition: index,
      })
    })
  }

  const collections: Array<{
    items: TemplateQuestion[]
    index: string
  }> = [
    {
      items: templateQuestions,
      index: 'questions',
    },
  ]

  return (
    <Box>
      <ExplotableList>
        <ExplotableItem
          scrollLimited={false}
          opened={open}
          renderCollectionHeader={() => questionsHeader(open)}
          toggle={toggle(!open)}
          fromCollection
        >
          {collections.map((collection, index) => (
            <SortableBoxList
              key={`collection_${index}`}
              items={collection.items}
              onSortEnd={onSortEnd}
              emptyMessage={i18n.t('surveys.form.question_configs.no_question_configs')}
              itemAsKey={(item) => item.attributes.id.toString()}
              isDisabled={() => (isCore ? true : false)}
              renderItem={(item) => (
                <Item
                  templateQuestion={item}
                  createTemplateQuestion={createTemplateQuestion}
                  updateTemplateQuestion={updateTemplateQuestion}
                  deleteTemplateQuestion={deleteTemplateQuestion}
                  isCore={isCore}
                />
              )}
            />
          ))}
        </ExplotableItem>
      </ExplotableList>
    </Box>
  )
}

export default observer(QuestionCategoryComponent)
